/**
 * Shared button variant styles for Button and LinkButton components.
 *
 * These variant classes are identical between Button and LinkButton,
 * except for disabled state handling:
 * - Button uses `disabled:` (for <button> elements)
 * - LinkButton uses `data-[disabled]:` (for <a> elements via react-aria)
 */

export const buttonVariants = {
  variant: {
    primary: `
      bg-interactive-primary-button-enabled-background
      hover:bg-interactive-primary-button-hover-background
      focus:bg-interactive-primary-button-focus-background
      disabled:bg-interactive-disabled-background disabled:text-interactive-disabled-foreground`,
    secondary: `
      border-interactive-secondary-button-enabled-outline
      text-interactive-secondary-button-enabled-foreground
      bg-interactive-secondary-button-enabled-background
      hover:bg-interactive-secondary-button-hover-background
      hover:text-interactive-secondary-button-hover-foreground
      hover:border-interactive-secondary-button-hover-outline
      focus:bg-interactive-secondary-button-focus-background
      focus:text-interactive-secondary-button-focus-foreground
      focus:border-interactive-secondary-button-focus-outline
      disabled:border-interactive-disabled-outline disabled:bg-transparent disabled:text-interactive-disabled-foreground`,
    tertiary: `
      text-interactive-tertiary-button-enabled-foreground
      bg-transparent
      hover:bg-interactive-tertiary-button-hover-background
      hover:text-interactive-tertiary-button-hover-foreground
      focus:text-interactive-tertiary-button-focus-foreground
      focus:bg-interactive-tertiary-button-focus-background
      disabled:text-interactive-disabled-foreground disabled:hover:bg-transparent`,
    glass: `
      bg-interactive-glass-button-enabled-background
      border-interactive-glass-button-enabled-outline
      text-interactive-glass-button-enabled-foreground
      hover:text-interactive-glass-button-hover-foreground
      hover:bg-interactive-glass-button-hover-background
      hover:border-interactive-glass-button-hover-outline
      focus:bg-interactive-glass-button-focus-background focus:text-interactive-glass-button-focus-foreground focus:border-interactive-glass-button-focus-outline backdrop-blur hover:backdrop-blur-xl  focus:backdrop-blur-2xl  disabled:bg-interactive-disabled-background disabled:text-interactive-disabled-foreground`,
    negative: `
      text-interactive-negative-button-enabled-foreground
      bg-interactive-negative-button-enabled-background
      hover:bg-interactive-negative-button-hover-background
      hover:text-interactive-negative-button-hover-foreground
      focus:text-interactive-negative-button-focus-foreground
      focus:bg-interactive-negative-button-focus-background
      disabled:text-interactive-disabled-foreground disabled:hover:bg-interactive-negative-button-enabled-background`,
    "app-selector": `
      flex items-center text-label-medium
      hover:bg-interactive-primary-button-enabled-background
      hover:text-interactive-primary-button-enabled-foreground
      outline-none justify-center py-1 px-3 gap-1 rounded-10 hover:border-interactive-primary-button-enabled-background bg-neutral-white transition text-interactive-secondary-button-enabled-foreground`,
    dropdown: `
    py-3xs px-3xs rounded-sm
    text-label-medium text-text-primary font-label-medium font-normal
    hover:bg-interactive-secondary-button-focus-background
    focus:bg-interactive-secondary-button-focus-background`,
    plain: `
      text-text-primary bg-transparent
      disabled:text-neutral-200`,
    link: `
      p-0 border-0 rounded-none font-normal bg-transparent
      text-interactive-link-enabled underline
      hover:text-interactive-link-hover
      focus:text-interactive-link-focus
      disabled:text-interactive-link-disabled disabled:cursor-not-allowed`,
  },
} as const;

export const buttonDefaultVariants = {
  variant: "primary" as const,
};

/**
 * Variant styles for LinkButton with data-[disabled]: instead of disabled:.
 * Since LinkButton uses <a> elements which don't support :disabled pseudo-class,
 * react-aria uses data-disabled attribute instead.
 *
 * These are written explicitly (not transformed at runtime) so that
 * Tailwind's JIT compiler can detect the class names during source scanning.
 */
export const linkButtonVariants = {
  variant: {
    primary: `
      bg-interactive-primary-button-enabled-background
      hover:bg-interactive-primary-button-hover-background
      focus:bg-interactive-primary-button-focus-background
      data-[disabled]:bg-interactive-disabled-background data-[disabled]:text-interactive-disabled-foreground`,
    secondary: `
      border-interactive-secondary-button-enabled-outline
      text-interactive-secondary-button-enabled-foreground
      bg-interactive-secondary-button-enabled-background
      hover:bg-interactive-secondary-button-hover-background
      hover:text-interactive-secondary-button-hover-foreground
      hover:border-interactive-secondary-button-hover-outline
      focus:bg-interactive-secondary-button-focus-background
      focus:text-interactive-secondary-button-focus-foreground
      focus:border-interactive-secondary-button-focus-outline
      data-[disabled]:border-interactive-disabled-outline data-[disabled]:bg-transparent data-[disabled]:text-interactive-disabled-foreground`,
    tertiary: `
      text-interactive-tertiary-button-enabled-foreground
      bg-transparent
      hover:bg-interactive-tertiary-button-hover-background
      hover:text-interactive-tertiary-button-hover-foreground
      focus:text-interactive-tertiary-button-focus-foreground
      focus:bg-interactive-tertiary-button-focus-background
      data-[disabled]:text-interactive-disabled-foreground  data-[disabled]:hover:bg-transparent`,
    glass: `
      bg-interactive-glass-button-enabled-background
      border-interactive-glass-button-enabled-outline
      text-interactive-glass-button-enabled-foreground
      hover:text-interactive-glass-button-hover-foreground
      hover:bg-interactive-glass-button-hover-background
      hover:border-interactive-glass-button-hover-outline
      focus:bg-interactive-glass-button-focus-background focus:text-interactive-glass-button-focus-foreground focus:border-interactive-glass-button-focus-outline backdrop-blur hover:backdrop-blur-xl  focus:backdrop-blur-2xl  data-[disabled]:bg-interactive-disabled-background data-[disabled]:text-interactive-disabled-foreground`,
    negative: `
      text-interactive-negative-button-enabled-foreground
      bg-interactive-negative-button-enabled-background
      hover:bg-interactive-negative-button-hover-background
      hover:text-interactive-negative-button-hover-foreground
      focus:text-interactive-negative-button-focus-foreground
      focus:bg-interactive-negative-button-focus-background
      data-[disabled]:text-interactive-disabled-foreground data-[disabled]:hover:bg-interactive-negative-button-enabled-background`,
    "app-selector": `
      flex items-center text-label-medium
      hover:bg-interactive-primary-button-enabled-background
      hover:text-interactive-primary-button-enabled-foreground
      outline-none justify-center py-1 px-3 gap-1 rounded-10 hover:border-interactive-primary-button-enabled-background bg-neutral-white transition text-interactive-secondary-button-enabled-foreground`,
    dropdown: `
    py-3xs px-3xs rounded-sm
    text-label-medium text-text-primary font-label-medium font-normal
    hover:bg-interactive-secondary-button-focus-background
    focus:bg-interactive-secondary-button-focus-background`,
    plain: `
      text-text-primary bg-transparent
      data-[disabled]:text-neutral-200`,
    link: `
      p-0 border-0 rounded-none font-normal bg-transparent
      text-interactive-link-enabled underline
      hover:text-interactive-link-hover
      focus:text-interactive-link-focus
      data-[disabled]:text-interactive-link-disabled data-[disabled]:cursor-not-allowed`,
  },
} as const;
